import React from 'react';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { isValid } from 'date-fns';

import styles from './time.style';

const Time = ({ value, name, error, onChange, ampm = false, ...rest }) => {
  const handleChange = (newValue) => {
    if (newValue && newValue instanceof Date && isValid(newValue)) {
      // Format as HH:mm for time-only fields
      const hours = String(newValue.getHours()).padStart(2, '0');
      const minutes = String(newValue.getMinutes()).padStart(2, '0');
      onChange(`${hours}:${minutes}`);
    } else {
      // Normalize invalid values to empty string to keep form state consistently a string
      onChange('');
    }
  };

  // Parse HH:mm string to Date object for the picker
  // Uses a fixed, DST-safe reference date (January 1st) to avoid DST transition issues
  const parseTimeValue = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return null;

    const [hoursPart, minutesPart] = timeString.split(':');
    if (hoursPart === undefined || minutesPart === undefined) return null;
    const hours = parseInt(hoursPart, 10);
    const minutes = parseInt(minutesPart, 10);
    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    // Use a fixed winter date (Jan 1) to avoid DST transition issues
    const date = new Date(2024, 0, 1);
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(0);
    date.setMilliseconds(0);

    return isValid(date) ? date : null;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TimePicker
        showToolbar={false}
        format="HH:mm"
        ampm={ampm}
        slotProps={{
          popper: {
            disablePortal: false,
            sx: styles.popper,
          },
          textField: {
            'data-testid': `input-${name}`,
            variant: 'outlined',
            error: !!error,
            sx: {
              ...styles.textfield,
              ...(!!error && styles.errorTextInput),
            },
          },
        }}
        value={parseTimeValue(value)}
        onChange={handleChange}
        {...rest}
        label=""
      />
    </LocalizationProvider>
  );
};

export default Time;
